import React, { useEffect, useState } from 'react';
import { useSpring, animated, interpolate } from 'react-spring';
import { useGesture } from 'react-with-gesture';
import { FocusOn } from 'react-focus-on';
import { usePrevious } from 'hooks/usePrevious';

import './Drawer.scss';

const renderSwipeable = (setYPosition, bind) => {
  return (
    <animated.div className="gesture-button-wrapper" {...bind()}>
      <button
        className="gesture-button"
        data-cy="stop-gallery__mobile-close-btn"
        aria-label="close gallery"
        onClick={setYPosition}
      >
        <div className="bar" />
      </button>
    </animated.div>
  );
};

const Drawer = React.memo(props => {
  const {
    yPosition,
    children,
    setRestPosition,
    setYPosition,
    restPosition,
  } = props;
  const [hideDrawerAccessibly, setHideDrawerAccessibly] = useState('none');
  const [bind, { delta, down, velocity, distance, direction }] = useGesture({
    threshold: 15,        // Require 15px movement before recognizing gesture
    preventDefault: false, // Don't prevent default scroll behavior
    passive: true,        // Make events passive to not block scrolling
    delay: 50            // Small delay to distinguish between scroll and swipe
  });
  const prevEvent = usePrevious({ down, velocity, direction });

  const { y } = useSpring({
    y: down && delta[1] > 0 ? delta[1] : yPosition,
    immediate: name => down && name === 'y',
    onRest: object => setRestPosition(object.y),
  });

  useEffect(() => {
    // handle swipe
    if (
      prevEvent?.down &&
      !down &&
      prevEvent.direction[1] > 0 &&
      prevEvent.velocity > 1.2
    ) {
      // set it one time
      yPosition !== window.innerHeight && setYPosition(window.innerHeight);
    }
    // close if dragged far enough
    if (prevEvent?.down && !down && distance >= window.innerHeight * 0.7) {
      // set it one time
      yPosition !== window.innerHeight && setYPosition(window.innerHeight);
    }
    if (restPosition === window.innerHeight) {
      setHideDrawerAccessibly('none');
      if (yPosition !== window.innerHeight) {
        setHideDrawerAccessibly('inherit');
      }
    }
  }, [
    down,
    delta,
    velocity,
    distance,
    direction,
    prevEvent,
    restPosition,
    yPosition,
    setYPosition,
  ]);

  const isMobile = window.innerWidth <= 1024;

  return (
    <animated.div
      className="drawer-content"
      style={{
        transform: interpolate([y], y => `translate3d(0,${y}px,0)`),
        display: hideDrawerAccessibly,
      }}
    >
      <FocusOn enabled={!yPosition} onEscapeKey={setYPosition}>
        {isMobile && renderSwipeable(setYPosition, bind)}
        {children}
      </FocusOn>
    </animated.div>
  );
});

export default Drawer;
